import { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core";
import { FormikHelpers, useFormik } from "formik";
import * as Yup from "yup";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import Alert from "@material-ui/lab/Alert";
import Modal from "@material-ui/core/Modal";
import { useIntl } from "react-intl";
import { FormattedMessage } from "react-intl";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";

import { InputField } from "../../../../../../components/input-field/InputField";
import { InputButton } from "../../../../../../components/input-button/InputButton";
import { BusinessOwnerService } from "../../../../../../services/BusinessOwnerService";

interface InviteManagerProps {
  businessId: number;
  isOpened: boolean;
  closeCallback: () => void;
}

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: "bold",
  },
  modalBody: {
    width: 416,
    padding: 10,
    border: "none",
  },
}));

export const InviteManager = (props: InviteManagerProps) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const { businessId, isOpened, closeCallback } = props;
  const [branches, setBranches] = useState([]);
  const [error, setError] = useState(false);
  const [succeed, setSucceed] = useState(false);

  const formik = useFormik({
    initialValues: {
      manager_email: "",
      branch_id: "",
    },
    validationSchema: Yup.object().shape({
      manager_email: Yup.string()
        .email(formatMessage({ id: "sharedScreen.enterValidEmail" }))
        .required(
          formatMessage({ id: "sharedScreen.pleaseEnterManagerEmail" })
        ),
      branch_id: Yup.string().required(
        formatMessage({ id: "sharedScreen.pleaseSelectTheBranch" })
      ),
    }),
    onSubmit: async (
      values,
      { setSubmitting, resetForm }: FormikHelpers<any>
    ) => {
      setError(false);
      try {
        const inviteManagerResult = await BusinessOwnerService.inviteManager({
          branchId: values.branch_id,
          managerEmail: values.manager_email,
        });
        if (
          inviteManagerResult.isSuccess &&
          inviteManagerResult.data.code === 100
        ) {
          resetForm({});
          setSucceed(true);
          setTimeout(() => {
            setSucceed(false);
          }, 2000);
        } else {
          setError(true);
        }
      } catch (error) {
        setError(true);
      }
      setSubmitting(false);
    },
  });

  useEffect(() => {
    const loadBranches = async () => {
      setBranches([]);
      try {
        const branches = await BusinessOwnerService.listBranches({
          businessId,
        });
        if (branches.isSuccess && branches.data.code === 100) {
          const responseData = JSON.parse(JSON.stringify(branches.data.data));
          setBranches(responseData.branches);
        }
      } catch (_) {}
    };
    loadBranches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal className={classes.modal} open={isOpened} onClose={closeCallback}>
      <Paper className={classes.modalBody}>
        <form noValidate onSubmit={formik.handleSubmit} autoComplete="off">
          <Grid container spacing={2}>
            <Grid
              item
              container
              direction="row"
              justify="space-between"
              alignItems="center"
              xs={12}
              sm={12}
              lg={12}
            >
              <Typography
                className={classes.modalTitle}
                variant="h4"
                component="h4"
              >
                <FormattedMessage id="sharedScreen.inviteAManager" />
              </Typography>
              <IconButton edge="end" onClick={closeCallback}>
                <HighlightOffIcon fontSize="large" />
              </IconButton>
            </Grid>
            {error && (
              <Grid item xs={12} sm={12} lg={12}>
                <Alert severity="error">
                  <FormattedMessage id="sharedScreen.thereWasAProblemSendingTheInvitation" />
                </Alert>
              </Grid>
            )}
            <Grid item xs={12} sm={12} lg={12}>
              <InputField
                value={formik.values.manager_email}
                fullWidth
                label={formatMessage({ id: "sharedScreen.managerEmail" })}
                name="manager_email"
                onChange={formik.handleChange}
                error={
                  formik.touched.manager_email &&
                  Boolean(formik.errors.manager_email)
                }
                helperText={
                  formik.touched.manager_email && formik.errors.manager_email
                }
              />
            </Grid>
            <Grid item xs={12} sm={12} lg={12}>
              <TextField
                select
                variant="outlined"
                size="small"
                value={formik.values.branch_id}
                label={formatMessage({ id: "sharedScreen.branch" })}
                fullWidth
                name="branch_id"
                onChange={formik.handleChange}
                error={
                  formik.touched.branch_id && Boolean(formik.errors.branch_id)
                }
                helperText={formik.touched.branch_id && formik.errors.branch_id}
              >
                {branches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    {branch.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={12} lg={12} container direction="row">
              <InputButton
                disabled={formik.isSubmitting}
                fullWidth
                type="submit"
                variant="contained"
                color="secondary"
              >
                <FormattedMessage id="sharedScreen.invite" />
                {formik.isSubmitting && <CircularProgress size={30} />}
              </InputButton>
            </Grid>
            {succeed && (
              <Grid item xs={12} sm={12} lg={12}>
                <Alert severity="success">
                  <FormattedMessage id="sharedScreen.invitationSentSuccessfully" />
                </Alert>
              </Grid>
            )}
          </Grid>
        </form>
      </Paper>
    </Modal>
  );
};
